import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthPage } from '@/pages/AuthPage';
import { CharactersPage } from '@/pages/CharactersPage';
import { CreateCharacterPage } from '@/pages/CreateCharacterPage';
import { StoryTemplatesPage } from '@/pages/StoryTemplatesPage';
import { RoleTemplatesPage } from '@/pages/RoleTemplatesPage';
import { StyleTemplatesPage } from '@/pages/StyleTemplatesPage';
import { CreateStyleTemplatePage } from '@/pages/CreateStyleTemplatePage';
import '@/index.css';
import { PrivateRoute } from '@/components/routing/PrivateRoute';
import { AdminRoute } from '@/components/routing/AdminRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CreateStoryPage } from '@/pages/CreateStoryPage';
import { CreateStoryTemplatePage } from '@/pages/CreateStoryTemplatePage';
import { CreateRoleTemplatePage } from '@/pages/CreateRoleTemplatePage';
import { CreateCustomTemplatePage } from '@/pages/CreateCustomTemplatePage';
import { GeneratedStoriesPage } from '@/pages/GeneratedStoriesPage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { WaitlistPage } from '@/pages/WaitlistPage';
import { ROUTES } from '@/routes';
import { buildRoute } from '@/routes/buildRoute';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/auth/AuthProvider';
import { initializeSentry } from '@/utils/sentry';
import { SentryErrorBoundary } from '@/components/SentryErrorBoundary';
import { SentryProvider } from '@/components/SentryProvider';

// Initialize Sentry first
initializeSentry();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SentryProvider>
            <Routes>
              {/* dashboard facing routes */}
              <Route
                path={ROUTES.HOME} // TODO: remove this once we have a home page
                element={<Navigate to={ROUTES.DASHBOARD_CHARACTERS} replace />}
              />
              <Route path={ROUTES.LOGIN} element={<AuthPage />} />
              <Route path={ROUTES.WAITLIST} element={<WaitlistPage />} />
              <Route path={ROUTES.DASHBOARD} element={<DashboardLayout />}>
                <Route
                  path={ROUTES.DASHBOARD_CHARACTERS}
                  element={
                    <PrivateRoute>
                      <CharactersPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_CHARACTERS_CREATE}
                  element={
                    <PrivateRoute>
                      <CreateCharacterPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_STORY_TEMPLATES}
                  element={
                    <PrivateRoute>
                      <StoryTemplatesPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_STORY_TEMPLATES_CREATE}
                  element={
                    <AdminRoute>
                      <CreateStoryTemplatePage />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_GENERATED_STORIES}
                  element={
                    <PrivateRoute>
                      <GeneratedStoriesPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_ROLE_TEMPLATES}
                  element={
                    <AdminRoute>
                      <RoleTemplatesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_STYLE_TEMPLATES}
                  element={
                    <AdminRoute>
                      <StyleTemplatesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_STYLE_TEMPLATES_CREATE}
                  element={
                    <AdminRoute>
                      <CreateStyleTemplatePage />
                    </AdminRoute>
                  }
                />
                <Route
                  path={ROUTES.DASHBOARD_ROLE_TEMPLATES_CREATE}
                  element={
                    <AdminRoute>
                      <CreateRoleTemplatePage />
                    </AdminRoute>
                  }
                />
              </Route>

              {/* end-user facing routes */}
              <Route
                path={ROUTES.CREATE_CUSTOM_TEMPLATE}
                element={
                  <PrivateRoute>
                    <CreateCustomTemplatePage />
                  </PrivateRoute>
                }
              />
              <Route
                path={buildRoute.createStory(':templateId')}
                element={
                  <PrivateRoute>
                    <CreateStoryPage />
                  </PrivateRoute>
                }
              />
              <Route
                path={ROUTES.CHECKOUT}
                element={
                  <PrivateRoute>
                    <CheckoutPage />
                  </PrivateRoute>
                }
              />
              <Route
                path={ROUTES.NOT_FOUND}
                element={
                  // TODO replace with 404 page
                  <div className="flex h-screen items-center justify-center">
                    404
                  </div>
                }
              />
            </Routes>
          </SentryProvider>
        </AuthProvider>
      </BrowserRouter>
    </SentryErrorBoundary>
    <Toaster />
  </React.StrictMode>
);
